import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import classNames from 'classnames';
import FraudAndCompliancesTab from './FraudAndCompliancesTab';
import { WithdrawalRequestsTab } from 'src/components/FraudAndCompliance/WithdrawalRequestsTab/WithdrawalRequestsTab';
import { AutomaticWithdrawals } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/AutomaticWithdrawals';
import { AffordabilityExcludedTab } from 'src/components/FraudAndCompliance/AffordabilityExcludedTab/AffordabilityExcludedTab';
import { PEPTab } from 'src/components/FraudAndCompliance/PEPTab/PEPTab';
import { GamstopTab } from 'src/components/FraudAndCompliance/GamstopTab/GamstopTab';
import { SelfExcludedTab } from 'src/components/FraudAndCompliance/SelfExcludedTab/SelfExcludedTab';
import { FraudAndComplianceTabItems } from 'src/components/FraudAndCompliance/FraudAndComplianceTabItems';
import { MohPopupTriggersTab } from 'src/components/FraudAndCompliance/MohPopupTriggersTab/MohPopupTriggersTab';
import { MainContainer } from 'src/domains/layouts/shared/layout/Layout.style';

interface PropsType {
    permissions: Map<string, Map<'constrains' | 'permission', Array<string> | string>>;
}

export const FraudAndCompliance = observer('FraudAndCompliance', (props: PropsType) => {
    const {
        routerBackoffice,
        configState: {
            config: { isAffordability, isPEP, withdrawalsTabsShow },
        },
    } = useAppStateContext();

    const { permissions } = props;

    const customerParams = routerBackoffice.getParametersString('global', 'tab') ?? 'manual-withdrawals';

    const permissionClass = {
        'not-permitted': permissions.get('notification/matched-acc-mgmt') === undefined,
    };

    return (
        <MainContainer>
            <FraudAndComplianceTabItems />

            {withdrawalsTabsShow && customerParams === 'manual-withdrawals' && <WithdrawalRequestsTab />}

            {withdrawalsTabsShow && customerParams === 'automatic-withdrawals' && <AutomaticWithdrawals />}

            {isAffordability && customerParams === 'affordability-excluded' && <AffordabilityExcludedTab />}

            {isPEP && customerParams === 'pep' && <PEPTab />}

            {permissions.get('notification/matched-acc-mgmt') !== undefined && customerParams === 'matched-account' && (
                <FraudAndCompliancesTab
                    entityKey='matched-account'
                    className={classNames(permissionClass)}
                    permissions={permissions}
                    dataTest='matched-accounts-tab'
                />
            )}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'payment-declines' && <FraudAndCompliancesTab entityKey='payment-declines' />}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'new-matched-payment-methods' && (
                    <FraudAndCompliancesTab
                        entityKey='new-matched-payment-methods'
                        title='Matched Payment Methods'
                    />
                )}

            {permissions.get('notification/matched-acc-mgmt') !== undefined && customerParams === 'review-account' && (
                <FraudAndCompliancesTab
                    entityKey='review-account'
                    title='Key Account Changes'
                />
            )}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'call-credit-age-verification' && (
                    <FraudAndCompliancesTab entityKey='call-credit-age-verification' />
                )}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'call-credit-validation-errors' && (
                    <FraudAndCompliancesTab
                        manualAv={{
                            isPep: isPEP,
                        }}
                        entityKey='call-credit-validation-errors'
                    />
                )}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'gamstop-account-not-longer-blocked' && <GamstopTab />}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'self-exclusion-changes' && <SelfExcludedTab />}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'moh-popup-triggers' && <MohPopupTriggersTab />}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'deposit-restrictions' && (
                    <FraudAndCompliancesTab
                        entityKey='deposit-restrictions'
                        title='Deposit Restrictions'
                        dataTest='deposit-restrictions-tab'
                    />
                )}

            {permissions.get('notification/matched-acc-mgmt') !== undefined &&
                customerParams === 'id-mission-verifications' && (
                    <FraudAndCompliancesTab
                        entityKey='id-mission-verifications'
                        title='EKYC'
                        dataTest='id-mission-verifications-tab'
                    />
                )}
        </MainContainer>
    );
});
